import { useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import type { IOption } from 'interfaces/select.interface'
import { useSearch, useFilter, useModal, useTableSelect } from 'shared/hooks'
import { useFetchSales } from './queries/useFetchSales'
import { useExportSaleMutation } from './mutations/useExportSaleMutation'
import { defaultConfig } from 'config/default'
import { downloadAs } from 'utils/downloadFile'
import { useRequestPayoutMutation } from './mutations/useRequestPayoutMutation'
import { useRequestCommissionMutation } from './mutations/useRequestCommissionMutation'
import {
	CardWrapper,
	ConfirmModal,
	EmptyTable,
	FormatModal,
	Header,
	SortSelect
} from 'shared/components'
import { SummarySection } from './organisms/SummarySection'
import { FiltersSection } from './organisms/FiltersSection/FiltersSection'
import { TableSection } from './organisms/TableSection'
import { SaleDetailsDialog } from 'features/sales/organisms/SaleDetailsDialog/SaleDetailsDialog'
import { useSortingFilters } from './hooks/useSortingFilters'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import type { SaleItem } from 'features/sales/types/sales.types'

const SalesPage = () => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const { handleSearch } = useSearch({ name: 'search' })
	const { handleFilter } = useFilter()
	const sortingFilters = useSortingFilters()
	const [checkedSales, setCheckedSales] = useState<number[]>([])
	const [selectedSale, setSelectedSale] = useState<SaleItem | null>(null)
	const [chosenExportFormat, setChosenExportFormat] = useState<number | string | null>(null)
	const { sales, salesLoading } = useFetchSales()
	const { select } = useTableSelect({ nodes: sales?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})

	const {
		isOpen: isCommissionModalOpen,
		openModal: openCommissionModal,
		closeModal: closeCommissionModal
	} = useModal()
	const {
		isOpen: isPayoutModalOpen,
		openModal: openPayoutModal,
		closeModal: closePayoutModal
	} = useModal()
	const {
		isOpen: isExportModalOpen,
		openModal: openExportModal,
		closeModal: closeExportModal
	} = useModal()
	const {
		isOpen: isFormatModalOpen,
		openModal: openFormatModal,
		closeModal: closeFormatModal
	} = useModal()

	const { requestPayoutMutation } = useRequestPayoutMutation(() => closePayoutModal())
	const { requestCommissionMutation } = useRequestCommissionMutation(() => closeCommissionModal())
	const { exportMutation, isExportLoading } = useExportSaleMutation({
		onSuccess: (res: any) => {
			const format = defaultConfig.exportFormatOptions
				.find((format) => format.value === chosenExportFormat)
				?.label.toLowerCase()
			if (format) {
				downloadAs({ file: res.data, name: 'sales-export', format })
			}
			setCheckedSales([])
		},
		onSettled: () => closeFormatModal()
	})

	const requestExport = () => {
		exportMutation({ checkedSales, chosenExportFormat })
	}

	const handleExportModal = () => {
		closeExportModal()
		openFormatModal()
	}

	const handleFormatChange = (data: IOption) => {
		data?.value?.toString() ? setChosenExportFormat(data.value) : setChosenExportFormat(null)
	}

	function onSelectChange(action: any, state: any) {
		setCheckedSales(state.ids)
	}

	const renderContent = () => {
		if (salesLoading) return <EmptyTable isLoading />
		if (!sales?.data?.results?.length)
			return <EmptyTable text={searchParams.toString() ? t('No results') : t('No sales yet')} />

		return (
			<TableSection
				sales={sales}
				salesLoading={salesLoading}
				select={select}
				onSelectSale={setSelectedSale}
			/>
		)
	}

	return (
		<>
			<Header
				title={t('Sales')}
				actions={<SortSelect {...sortingFilters} onChange={handleFilter} />}
			/>
			<div className="space-y-5 px-5 lg:flex lg:max-h-[calc(100vh-72px-4rem)] lg:flex-col lg:px-0">
				<SummarySection summary={sales?.data?.summary} />
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<FiltersSection
						payoutAction={openPayoutModal}
						commissionAction={openCommissionModal}
						exportAction={openExportModal}
						checkedSales={checkedSales}
						handleSearch={handleSearch}
						handleFilter={handleFilter}
					/>
					{renderContent()}
				</CardWrapper>
			</div>

			<SaleDetailsDialog sale={selectedSale} closeModal={() => setSelectedSale(null)} />

			{isCommissionModalOpen && (
				<ConfirmModal
					title={t('Recalculate commission')}
					isOpened={isCommissionModalOpen}
					closeModal={closeCommissionModal}
					confirm={() => requestCommissionMutation({ checkedSales })}>
					<p>
						{t('You are about to recalculate multiple sales. Are you sure you want to proceed?')}
					</p>
				</ConfirmModal>
			)}
			{isPayoutModalOpen && (
				<ConfirmModal
					title={t('Request payout')}
					isOpened={isPayoutModalOpen}
					closeModal={closePayoutModal}
					confirm={() => requestPayoutMutation({ checkedSales })}>
					<p>
						{t(
							'You are about to request multiple sales payouts. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isExportModalOpen && (
				<ConfirmModal
					title={t('Export selected Sales')}
					isOpened={isExportModalOpen}
					closeModal={closeExportModal}
					confirm={handleExportModal}>
					<p>{t('You are about to export multiple sales. Are you sure you want to proceed?')}</p>
				</ConfirmModal>
			)}
			{isFormatModalOpen && (
				<FormatModal
					disableButton={chosenExportFormat === null}
					isOpened={isFormatModalOpen}
					closeModal={closeFormatModal}
					confirm={requestExport}
					isLoading={isExportLoading}
					onChange={handleFormatChange}
				/>
			)}
		</>
	)
}

export default SalesPage
